export type PopupScreenMetrics = {
  screenX: number;
  screenY: number;
  outerWidth: number;
  outerHeight: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
};

export type PopupSize = {
  width: number;
  height: number;
};

/**
 * Requests a centered popup. Browsers may ignore `left`/`top`.
 * Placement is relative to the current window, then clamped when that
 * window sits on the reported available screen.
 */
export function centeredPopupFeatures(
  size: PopupSize,
  metrics: PopupScreenMetrics,
): string {
  const width = Math.max(1, Math.round(size.width));
  const height = Math.max(1, Math.round(size.height));
  let left = Math.round(metrics.screenX + (metrics.outerWidth - width) / 2);
  let top = Math.round(metrics.screenY + (metrics.outerHeight - height) / 2);

  const windowOnAvail =
    metrics.screenX >= metrics.availLeft - 8 &&
    metrics.screenX <= metrics.availLeft + metrics.availWidth;
  if (windowOnAvail && metrics.availWidth > 0 && metrics.availHeight > 0) {
    const maxLeft = metrics.availLeft + Math.max(0, metrics.availWidth - width);
    const maxTop = metrics.availTop + Math.max(0, metrics.availHeight - height);
    left = Math.min(Math.max(left, metrics.availLeft), maxLeft);
    top = Math.min(Math.max(top, metrics.availTop), maxTop);
  }

  return [
    "popup=yes",
    `width=${width}`,
    `height=${height}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    "menubar=no",
    "toolbar=no",
    "location=no",
    "status=no",
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");
}

export function readPopupScreenMetrics(): PopupScreenMetrics {
  const screenX = window.screenX ?? window.screenLeft ?? 0;
  const screenY = window.screenY ?? window.screenTop ?? 0;
  const screen = window.screen as Screen & { availLeft?: number; availTop?: number };
  return {
    screenX,
    screenY,
    outerWidth: window.outerWidth || window.innerWidth || 1024,
    outerHeight: window.outerHeight || window.innerHeight || 768,
    availLeft: screen.availLeft ?? 0,
    availTop: screen.availTop ?? 0,
    availWidth: screen.availWidth ?? (window.outerWidth || 1024),
    availHeight: screen.availHeight ?? (window.outerHeight || 768),
  };
}

export function openCenteredPopup(
  url: string,
  name: string,
  size: PopupSize,
): Window | null {
  return window.open(url, name, centeredPopupFeatures(size, readPopupScreenMetrics()));
}
